import { DateTime } from 'luxon'

import { BidType, MARKET_TIMEZONE, MFRR_MARKET_PROGRAMS } from '@/features/bidding/constants'
import type {
  BidMetaData,
  ExtendedBid,
  ExtendedBidPtu,
  OfferedAndForecastAndSuggestedPtu,
} from '@/features/bidding/types/bid'
import type { BidHistory } from '@/features/bidding/types/bidHistory'
import { findExpiredPtuPrefillSource, findFuturePtuPrefillBid } from '@/features/bidding/utils/ptuPrefillSources'
import { isSinglePriceStepPartner } from '@/features/bidding/utils/validation/priceStepValidationUtils'

import { capacityPtuFor } from './capacityPtuFor'
import { getLatestExtendedBid } from './getLatestBid'
import { getLatestCapacityBidOnThisDay, hasCapacityBidOnThisDay } from './hasCapacityBid'
import { isPtuEditable } from './isPtuEditable'

type GetInitialBidPtusParams = {
  bidMetaData: BidMetaData
  capacityBidHistories: BidHistory[]
  bidHistories: BidHistory[]
  ptus: OfferedAndForecastAndSuggestedPtu[]
  latestNonDraftBids: Map<string, ExtendedBid | null>
  acceptedBids: Map<string, ExtendedBid | null>
}

/**
 * Get the bid version that future PTUs are prefilled from.
 *
 * For mFRR ENERGY bids, the trader's draft wins and otherwise the current offer does - see
 * findFuturePtuPrefillBid. Every other bid type uses the newest version by createdAt, whatever
 * bucket it sits in, which is what the pre-partials bid-history supports: there createdAt is the
 * version proxy, since each submission is its own row.
 */
function getLatestBid(
  bidHistories: BidHistory[],
  activationGroupUuid: string,
  isMfrrEnergyBid: boolean,
): ExtendedBid | undefined {
  const historyForGroup = bidHistories.find((history) => history.activationGroupUuid === activationGroupUuid)

  if (!historyForGroup || historyForGroup.bids.length === 0) {
    return undefined
  }

  return isMfrrEnergyBid ? findFuturePtuPrefillBid(historyForGroup.bids) : getLatestExtendedBid(historyForGroup.bids)
}

/**
 * Prefill a PTU from a single bid version, taking both the volume and the price chunks from that
 * same version.
 *
 * Keeping the two together matters: the volume and the chunks are submitted as one PTU, and the
 * price step is seeded from `offeredPtuChunks` further down the flow (draft creation and the price
 * page). Mixing a volume from one bid version with the chunks of another produces a PTU whose total
 * does not match its own ladder, which blocks the whole delivery day on the price page with
 * "The offered volume for one or more PTUs is invalid". See BID-252.
 */
function prefillFromBidPtu(
  ptu: OfferedAndForecastAndSuggestedPtu,
  sourcePtu: ExtendedBidPtu,
  quantity: number,
): OfferedAndForecastAndSuggestedPtu {
  return {
    ...ptu,
    offeredVolume: {
      quantity: quantity,
      unit: ptu.offeredVolume.unit,
    },
    offeredPtuChunks: sourcePtu.ptuChunks,
  }
}

function findPtu(bid: ExtendedBid, ptuStart: DateTime): ExtendedBidPtu | undefined {
  return bid.ptus.find((bidPtu) => bidPtu.ptu.start.equals(ptuStart))
}

/**
 * Initialize bid PTUs with appropriate prefill logic based on market and bid type.
 *
 * For editable (future) PTUs:
 * - **mFRR Nordic ENERGY bids**: 3-tier priority
 *   1. Latest bid values (if exists) - checked in bidHistories
 *   2. Offered capacity bid values (if exists) - Finland/Norway/single-price-step-partner requirement, checked in capacityBidHistories
 *   3. Basis values
 *
 * - **All other markets/bid types**: 2-tier priority
 *   1. Latest bid values (if exists) - checked in bidHistories
 *   2. Basis values
 *
 * For expired PTUs (BID-209), when the accepted schedule is available:
 * - Use the latest ACCEPTED version, which is the TSO's truth for rows the trader cannot edit.
 *   Prefilling those from the latest bid instead means a rejected version's volumes are re-sent for
 *   a PTU in the past, which Portfolio Manager refuses.
 * - Fall back to the latest non-draft bid when no accepted version covers the PTU (nothing was ever
 *   acknowledged for it), which is the best available approximation.
 *
 * @param bidMetaData - Metadata for the bid being initialized
 * @param capacityBidHistories - Capacity bid histories (used only for mFRR Finland ENERGY fallback)
 * @param bidHistories - All bid histories for this activation group (contains all bid types including drafts)
 * @param ptus - Base PTU data from bid basis
 * @param latestNonDraftBids - Latest non-draft bids (fallback for expired PTUs)
 * @param acceptedBids - Latest ACCEPTED bid version per activation group (for expired PTUs)
 */
export function generateInitialBidPtus({
  bidMetaData,
  capacityBidHistories,
  bidHistories,
  ptus,
  latestNonDraftBids,
  acceptedBids,
}: GetInitialBidPtusParams) {
  // Only mFRR ENERGY PTUs ever expire (see isPtuEditable), so it is the only bid type whose prefill
  // sources BID-209 changes. Everything else keeps the rule it has always had.
  const isMfrrEnergyBid =
    bidMetaData.bidType === BidType.ENERGY && MFRR_MARKET_PROGRAMS.includes(bidMetaData.marketProgram)

  // The prefill sources are the same for every PTU, so they are resolved once
  const latestBid = getLatestBid(bidHistories, bidMetaData.activationGroupUuid, isMfrrEnergyBid)
  const acceptedBid = acceptedBids.get(bidMetaData.activationGroupUuid)
  const latestNonDraftBid = latestNonDraftBids.get(bidMetaData.activationGroupUuid)

  return ptus.map((ptu): OfferedAndForecastAndSuggestedPtu => {
    const ptuStart = ptu.ptu.start
    const isEditable = isPtuEditable(
      DateTime.now().setZone(MARKET_TIMEZONE),
      ptuStart,
      bidMetaData.bidType,
      bidMetaData.marketProgram,
    )

    if (isEditable) {
      // Priority 1: Check for latest bid values (all markets and bid types)
      // bidHistories contains all bid types (ENERGY, CAPACITY, etc.) for this activation group
      if (latestBid) {
        // Find the PTU in the latest bid
        const latestBidPtu = findPtu(latestBid, ptuStart)
        if (latestBidPtu) {
          return prefillFromBidPtu(ptu, latestBidPtu, latestBidPtu.offeredVolume.quantity)
        }
      }

      // Priority 2: For mFRR Nordics ENERGY bids ONLY, fall back to accepted capacity bid
      // This is a Nordic-specific requirement where ENERGY bids prefill from CAPACITY bid's offered volume
      // capacityBidHistories is a separate fetch specifically for CAPACITY bids
      const isMfrrFinlandEnergyBid =
        bidMetaData.bidType === BidType.ENERGY &&
        MFRR_MARKET_PROGRAMS.includes(bidMetaData.marketProgram) &&
        bidMetaData.portfolio.countryCode.toLowerCase() === 'fi'

      const isMfrrSinglePriceStepPartnerEnergyBid =
        bidMetaData.bidType === BidType.ENERGY &&
        MFRR_MARKET_PROGRAMS.includes(bidMetaData.marketProgram) &&
        isSinglePriceStepPartner(bidMetaData.portfolio.partnerCode)

      const isMfrrNorwayEnergyBid =
        bidMetaData.bidType === BidType.ENERGY &&
        MFRR_MARKET_PROGRAMS.includes(bidMetaData.marketProgram) &&
        bidMetaData.portfolio.countryCode.toLowerCase() === 'no'

      if (isMfrrFinlandEnergyBid || isMfrrSinglePriceStepPartnerEnergyBid || isMfrrNorwayEnergyBid) {
        const shouldPrefillWithOfferedCapacity = hasCapacityBidOnThisDay(
          capacityBidHistories,
          bidMetaData.activationGroupUuid,
        )
        if (shouldPrefillWithOfferedCapacity) {
          const capacityBid = getLatestCapacityBidOnThisDay(capacityBidHistories, bidMetaData.activationGroupUuid)
          if (capacityBid) {
            const capacityPtu = capacityPtuFor(capacityBid, ptu.ptu)
            const offeredQuantity = capacityPtu?.offeredVolume?.quantity
            if (offeredQuantity !== undefined) {
              return {
                ...ptu,
                offeredVolume: {
                  quantity: offeredQuantity,
                  unit: ptu.offeredVolume.unit,
                },
              }
            }
          }
        }
      }

      // Priority 3 (mFRR Nordic ENERGY) / Priority 2 (all others): Use basis values
      return ptu
    }

    // Expired PTUs: the trader cannot edit these rows, so they are prefilled from the accepted
    // version (the TSO's truth) rather than from the latest offer, which may have been rejected.
    const expiredPtuSource = findExpiredPtuPrefillSource(ptuStart, acceptedBid, latestNonDraftBid)

    if (!expiredPtuSource) {
      // Neither the accepted nor the latest sent version covers this PTU, so keep the basis value
      return ptu
    }

    return prefillFromBidPtu(ptu, expiredPtuSource, expiredPtuSource.offeredVolume.quantity)
  })
}
